import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
    GetVotingInformationBasis,
    GetVotingInformationBasisVariables
} from "src/api/generated/GetVotingInformationBasis";
import {
    InsertVotingInformationBasis,
    InsertVotingInformationBasisVariables
} from "src/api/generated/InsertVotingInformationBasis";
import {
    DELETE_VOTING,
    GET_VOTING_DETAILS,
    GET_VOTING_INFORMATION_BASIS,
    GET_VOTING_OPTIONS,
    GET_VOTING_TENANTS_OPTIONS_RESULTS,
    GET_VOTINGS_BY_AGENDAITEMID,
    INSERT_VOTING_INFORMATION_BASIS,
    INSERT_VOTING_OPTION_AGENDAITEM,
    UPSERT_VOTING_AGENDA_RESULTS
} from "src/api/voting";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { v4 as uuidv4 } from "uuid";
import i18next from "i18next";
import { InsertVotingOptions, InsertVotingOptionsVariables } from "src/api/generated/InsertVotingOptions";
import { DeleteVoting, DeleteVotingVariables } from "src/api/generated/DeleteVoting";
import {
    GetVotingsByAgendaitemId,
    GetVotingsByAgendaitemIdVariables
} from "src/api/generated/GetVotingsByAgendaitemId";
import {
    GetVotingTenantsAndOptionsAndResults,
    GetVotingTenantsAndOptionsAndResultsVariables
} from "src/api/generated/GetVotingTenantsAndOptionsAndResults";
import {
    UpsertVotingAgendaitemResult,
    UpsertVotingAgendaitemResultVariables
} from "src/api/generated/UpsertVotingAgendaitemResult";
import { GetVotingDetails, GetVotingDetailsVariables } from "src/api/generated/GetVotingDetails";
import { GetVotingOptions, GetVotingOptionsVariables } from "src/api/generated/GetVotingOptions";

interface IQuorums {
    key: number;
    label: string;
}

interface IKeys {
    keyid: string;
    name: string;
}

export interface IVotingBasis {
    votingId: string;
    title: string;
    question: string;
    quorum: number;
    keys: string;
    veto: boolean;
    votingMlsId: string;
}

export interface IVoting {
    votingid: string;
    votingagendaitemid: string;
    question: string;
    title: string;
}

export interface IVotingTenant {
    tenantid: string;
    name: string;
    unitvalue: number;
}

export interface IVotingOptions {
    votingoptionid: string;
    text: string;
}

export interface IVotingResults {
    id: string;
    state: number;
    unitid: string;
    votingagendaitemrealestateid: string;
    tenantid: string;
    votingoptionid: string | null;
}

export class VotingStore {
    rootStore: RootStore;
    isVotingModalDisplayed: boolean = false;

    quorums: IQuorums[] = [];
    keys: IKeys[] = [];

    votingBasis: IVotingBasis = {
        votingId: "",
        title: "",
        question: "",
        quorum: 0,
        keys: "",
        veto: false,
        votingMlsId: ""
    };

    votingList: IVoting[] = [];
    selectedVoting: IVoting = {
        votingid: "",
        votingagendaitemid: "",
        title: "",
        question: ""
    };

    votingOptionsDefault: string[] = [i18next.t("labels.yes"), i18next.t("labels.no"), i18next.t("labels.abstrain")];
    selectedRealestateIds: string[] = [];

    lastLoadedAgendaitemId: string | null = "";
    lastLoadedVotingId: string = "";

    votingTenants: IVotingTenant[] = [];
    votingOptions: IVotingOptions[] = [];
    votingResults: IVotingResults[] = [];

    votingComponentResponse: { id: string; selectedColumn: number | null }[] = [];

    isNewVoting: boolean = true;
    selectedVotingId: string = "";
    votingColors: string[] = ["#194d00", "#f8a300", "#900000", "#004997", "#650097"];

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isVotingModalDisplayed: observable,
            quorums: observable,
            keys: observable,
            votingBasis: observable,
            votingOptionsDefault: observable,
            selectedRealestateIds: observable,
            votingList: observable,
            selectedVoting: observable,
            lastLoadedAgendaitemId: observable,
            lastLoadedVotingId: observable,
            votingTenants: observable,
            votingOptions: observable,
            votingComponentResponse: observable,
            votingResults: observable,
            isNewVoting: observable,
            selectedVotingId: observable,
            votingColors: observable,

            hasVotingResultChanged: computed,

            loadVotingInformationBasis: action,
            setIsVotingModalDisplayed: action,
            setQuorums: action,
            setKeys: action,
            updateQuorum: action,
            updateKey: action,
            updateVeto: action,
            updateVotingBasisField: action,
            resetVotingBasis: action,
            saveVotingInformationBasis: action,
            setVotingOptionsDefault: action,
            setVotingOptions: action,
            setSelectedRealestateIds: action,
            saveVotingOptions: action,
            deleteVotingById: action,
            resetAllVotingData: action,
            loadAllVotingsByAgendaItemId: action,
            setSelectedVoting: action,
            loadVotingTenantsAndOptions: action,
            saveVotingResult: action,
            setVotingComponentResponse: action,
            setIsNewVoting: action,
            loadVotingDetails: action,
            setSelectedVotingId: action,
            loadVotingOptions: action
        });
    }

    get hasVotingResultChanged(): boolean {
        return this.votingComponentResponse.some(({ id, selectedColumn }) => {
            const original = this.votingResults.find((r) => r.tenantid === id);
            const originalOptionId = original?.votingoptionid;
            const originalIndex = originalOptionId
                ? this.votingOptions.findIndex((opt) => opt.votingoptionid === originalOptionId)
                : -1;

            const originalSelected = originalIndex >= 0 ? originalIndex + 1 : null;

            return originalSelected !== selectedColumn;
        });
    }

    /* ACTIONS */

    loadVotingDetails = async (votingId: string, agendaitemId: string) => {
        try {
            const { data }: { data: GetVotingDetails } = await apolloClientInstance.query<
                GetVotingDetails,
                GetVotingDetailsVariables
            >({
                query: GET_VOTING_DETAILS,
                fetchPolicy: "no-cache",
                variables: {
                    agendaitem: agendaitemId,
                    votingid: votingId
                }
            });

            runInAction(() => {
                if (data.survey_votings && data.survey_votings.length > 0) {
                    if (data.survey_votings[0].id === this.votingBasis.votingId) {
                        this.updateVotingBasisField("keys", data.survey_votings[0].key1id);
                        this.updateVotingBasisField("quorum", data.survey_votings[0].quorum);
                        this.updateVeto(data.survey_votings[0].veto || false);

                        if (data.survey_votings[0].voting_mls && data.survey_votings[0].voting_mls.length > 0) {
                            this.updateVotingBasisField("votingMlsId", data.survey_votings[0].voting_mls[0].id);
                        }

                        if (
                            data.survey_votings[0].votingagendaitems &&
                            data.survey_votings[0].votingagendaitems.length > 0 &&
                            data.survey_votings[0].votingagendaitems[0].votingagendaitemrealestates
                        ) {
                            const realestateIds =
                                data.survey_votings[0].votingagendaitems[0].votingagendaitemrealestates.map(
                                    (r) => r.realestateid
                                );

                            this.setSelectedRealestateIds(realestateIds);
                        }
                    }
                }
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingTenantsAndOptions = async () => {
        if (this.lastLoadedVotingId === this.selectedVoting.votingid) return;
        this.lastLoadedVotingId = this.selectedVoting.votingid;

        try {
            const { data }: { data: GetVotingTenantsAndOptionsAndResults } = await apolloClientInstance.query<
                GetVotingTenantsAndOptionsAndResults,
                GetVotingTenantsAndOptionsAndResultsVariables
            >({
                query: GET_VOTING_TENANTS_OPTIONS_RESULTS,
                fetchPolicy: "no-cache",
                variables: {
                    votingagendaitemid: this.selectedVoting.votingagendaitemid,
                    votingid: this.selectedVoting.votingid
                }
            });

            runInAction(() => {
                this.votingTenants = data.survey_v_votingtenantdetails.map((tenant) => ({
                    tenantid: tenant.tenantid ?? "",
                    name: tenant.name ?? "",
                    unitvalue: tenant.unitvalue ?? 0
                }));

                this.votingOptions = data.survey_votingoptions
                    .filter((option) => option.votingoption_mls.length > 0) // only keep items with at least one mls entry
                    .map((option) => {
                        const mls = option.votingoption_mls[0];
                        return {
                            votingoptionid: mls.votingoptionid ?? "",
                            text: mls.text
                        };
                    });

                this.votingResults = data.survey_votingagendaitemresults.map((result) => ({
                    id: result.id ?? "",
                    state: result.state ?? 0,
                    tenantid: result.tenantid ?? "",
                    unitid: result.unitid ?? "",
                    votingagendaitemrealestateid: result.votingagendaitemrealestateid ?? "",
                    votingoptionid: result.votingoptionid
                }));
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingOptions = async () => {
        try {
            const { data }: { data: GetVotingOptions } = await apolloClientInstance.query<
                GetVotingOptions,
                GetVotingOptionsVariables
            >({
                query: GET_VOTING_OPTIONS,
                fetchPolicy: "no-cache",
                variables: {
                    votingid: this.selectedVotingId
                }
            });

            runInAction(() => {
                this.votingOptions = data.survey_votingoptions
                    .filter((option) => option.votingoption_mls.length > 0) // only keep items with at least one mls entry
                    .map((option) => {
                        const mls = option.votingoption_mls[0];
                        return {
                            votingoptionid: mls.votingoptionid ?? "",
                            text: mls.text
                        };
                    });
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadAllVotingsByAgendaItemId = async (agendaitemId: string) => {
        if (this.lastLoadedAgendaitemId === agendaitemId) return;
        this.lastLoadedAgendaitemId = agendaitemId;

        try {
            const { data }: { data: GetVotingsByAgendaitemId } = await apolloClientInstance.query<
                GetVotingsByAgendaitemId,
                GetVotingsByAgendaitemIdVariables
            >({
                query: GET_VOTINGS_BY_AGENDAITEMID,
                fetchPolicy: "no-cache",
                variables: {
                    agendaitemid: agendaitemId
                }
            });

            runInAction(() => {
                this.votingList = data.survey_v_votings_by_agendaitem.map((v) => ({
                    votingid: v.votingid,
                    votingagendaitemid: v.votingagendaitemid,
                    question: v.question ?? "",
                    title: v.title ?? ""
                }));

                if (this.votingList.length > 0) {
                    this.setSelectedVoting(this.votingList[0]);

                    // this.loadVotingTenantsAndOptions();
                }
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingInformationBasis = async () => {
        try {
            const { data }: { data: GetVotingInformationBasis } = await apolloClientInstance.query<
                GetVotingInformationBasis,
                GetVotingInformationBasisVariables
            >({
                query: GET_VOTING_INFORMATION_BASIS,
                fetchPolicy: "no-cache"
            });

            runInAction(() => {
                const validQuorums: IQuorums[] = data.common_enumerations.map((item) => ({
                    key: item.key ?? -1,
                    label: item.label ?? ""
                }));

                const validKeys: IKeys[] = data.common_key_mls.map((item) => ({
                    keyid: item.keyid ?? "",
                    name: item.name ?? ""
                }));

                this.setQuorums(validQuorums);
                this.setKeys(validKeys);
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    updateQuorum = (quorumKey: number) => {
        this.votingBasis.quorum = quorumKey;
    };

    updateKey = (keyId: string) => {
        this.votingBasis.keys = keyId;
    };

    updateVeto = (veto: boolean) => {
        this.votingBasis.veto = veto;
    };

    updateVotingBasisField = <K extends keyof IVotingBasis>(field: K, value: IVotingBasis[K]) => {
        this.votingBasis[field] = value;
    };

    resetVotingBasis = () => {
        this.votingBasis = {
            votingId: "",
            title: "",
            question: "",
            quorum: 0,
            keys: "",
            veto: false,
            votingMlsId: ""
        };
    };

    resetAllVotingData = () => {
        this.resetVotingBasis();
        this.setVotingOptionsDefault([i18next.t("labels.yes"), i18next.t("labels.no"), i18next.t("labels.abstrain")]);
        this.setSelectedRealestateIds([]);
    };

    saveVotingInformationBasis = async () => {
        if (this.votingBasis.votingId.length <= 0) {
            this.updateVotingBasisField("votingId", uuidv4());
        }

        const insertBody = {
            objects: [
                {
                    id: this.votingBasis.votingId,
                    key1id: this.votingBasis.keys.length > 0 ? this.votingBasis.keys : null,
                    key2id: null,
                    quorum: this.votingBasis.quorum,
                    veto: this.votingBasis.veto,
                    voting_mls: {
                        on_conflict: {
                            constraint: "PK_voting_mls",
                            update_columns: ["title", "question"]
                        },
                        data: [
                            {
                                id: this.votingBasis.votingMlsId.length > 0 ? this.votingBasis.votingMlsId : uuidv4(),
                                title: this.votingBasis.title,
                                question: this.votingBasis.question,
                                language: i18next.language
                            }
                        ]
                    }
                }
            ]
        };

        try {
            await apolloClientInstance.mutate<InsertVotingInformationBasis, InsertVotingInformationBasisVariables>({
                mutation: INSERT_VOTING_INFORMATION_BASIS,
                variables: insertBody
            });
        } catch (error: any) {
            console.error(i18next.t("screens.assemblies.agenda.voting_save_failed: ", error));
        }
    };

    saveVotingOptions = async () => {
        const votingId = this.votingBasis.votingId;
        const votingOptions = this.isNewVoting ? this.votingOptionsDefault : this.votingOptions.map((opt) => opt.text);
        const selectedRealestateIds = this.selectedRealestateIds;

        const agendaitemId = this.rootStore.agendaitemStore.currentAgendaItem?.id;
        const assemblyId = this.rootStore.assemblyStore.currentAssembly?.id;

        if (!votingId || !agendaitemId || !assemblyId) {
            console.error("Missing required data: votingId, agendaitemId or assemblyId");
            return;
        }

        const insertBody = {
            votingid: votingId,
            objectVotingOptions: votingOptions.map((text) => ({
                votingid: votingId,
                votingoption_mls: {
                    data: [
                        {
                            text,
                            language: i18next.language || "de"
                        }
                    ]
                }
            })),
            objectVotingAgendaitems: [
                {
                    votingid: votingId,
                    agendaitemid: agendaitemId,
                    assemblyid: assemblyId,
                    votingagendaitemrealestates: {
                        data: selectedRealestateIds.map((realestateid) => ({ realestateid }))
                    }
                }
            ]
        };

        try {
            await apolloClientInstance.mutate<InsertVotingOptions, InsertVotingOptionsVariables>({
                mutation: INSERT_VOTING_OPTION_AGENDAITEM,
                variables: insertBody
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.agenda.voting_save_success"),
                MessageType.SUCCESS
            );

            runInAction(() => {
                this.loadAllVotingsByAgendaItemId(agendaitemId);
                // set the currentagendaitem -> hasVotings true where agendaitemId
            });
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.agenda.voting_save_failed"),
                MessageType.ERROR
            );
            console.error(i18next.t("screens.assemblies.agenda.voting_save_failed: ", error));
            this.deleteVotingById(this.votingBasis.votingId);
        }
        this.resetAllVotingData();
    };

    deleteVotingById = async (votingid: string) => {
        try {
            const idToDelete = votingid ?? this.votingBasis.votingId;

            if (idToDelete !== undefined) {
                await apolloClientInstance.mutate<DeleteVoting, DeleteVotingVariables>({
                    mutation: DELETE_VOTING,
                    variables: { votingId: idToDelete }
                });

                runInAction(() => {
                    this.votingList = this.votingList.filter((voting) => voting.votingid !== idToDelete);
                });
            }
        } catch (error: any) {
            console.error("Voting failed to delete. Error: ", error);
        }
    };

    saveVotingResult = async () => {
        const votingOptions = this.votingOptions;
        const responses = this.votingComponentResponse;

        if (this.votingResults.length === 0 || votingOptions.length === 0) {
            console.error("Missing voting results or voting options.");
            return;
        }

        const updatedResults = this.votingResults.map((result) => {
            // Find the response for this tenant
            const response = responses.find((r) => r.id === result.tenantid);

            let votingoptionid: string | null = null;

            if (response && response.selectedColumn !== null) {
                const index = response.selectedColumn - 1;
                votingoptionid = votingOptions[index]?.votingoptionid ?? null;
            }

            return {
                ...result,
                votingoptionid
            };
        });

        const insertBody = {
            votingagendaitemresults: updatedResults
        };

        try {
            await apolloClientInstance.mutate<UpsertVotingAgendaitemResult, UpsertVotingAgendaitemResultVariables>({
                mutation: UPSERT_VOTING_AGENDA_RESULTS,
                variables: insertBody
            });

            runInAction(() => {
                this.votingResults = updatedResults;
                this.votingComponentResponse = [];
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.agenda.voting_result_success"),
                MessageType.SUCCESS
            );
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.agenda.voting_result_failed"),
                MessageType.ERROR
            );
            console.error("Error setTenantVotingResult: ", error);
        }
    };

    /* SETTERS */

    setIsVotingModalDisplayed = (isVotingModalDisplayed: boolean) => {
        this.isVotingModalDisplayed = isVotingModalDisplayed;
    };

    setQuorums = (quorums: IQuorums[]) => {
        this.quorums = quorums;
    };

    setKeys = (keys: IKeys[]) => {
        this.keys = keys;
    };

    setVotingOptionsDefault = (votingOptionsDefault: string[]) => {
        this.votingOptionsDefault = votingOptionsDefault;
    };

    setVotingOptions = (votingOptions: IVotingOptions[]) => {
        this.votingOptions = votingOptions;
    };

    setSelectedRealestateIds = (selectedRealestateIds: string[]) => {
        this.selectedRealestateIds = selectedRealestateIds;
    };

    setSelectedVoting = (selectedVoting: IVoting) => {
        if (this.selectedVoting.votingid !== selectedVoting.votingid) {
            this.selectedVoting = selectedVoting;
        }
    };

    setVotingComponentResponse = (response: { id: string; selectedColumn: number | null }[]) => {
        this.votingComponentResponse = response;
    };

    setIsNewVoting = (isNewVoting: boolean) => {
        this.isNewVoting = isNewVoting;
    };

    setSelectedVotingId = (selectedVotingId: string) => {
        this.selectedVotingId = selectedVotingId;
    };
}
